import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReactFlow, Background, Controls, MarkerType } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { createEntity, friendlyError, updateEntity, useEntityList } from '../data/entities';
import { requirementProblems, requirementSummary } from '../data/requirements';
import RequirementsEditor, { StatusChip } from './RequirementsEditor';
import { describeSolution } from '../data/describe';
import { FOCUS_EVENT } from '../utils/pageControls';
import { useAuth } from '../AuthContext';

// Solution engineering ([[solution-engineering]]): a solution is the whole
// business service — App Builder apps and nQube workflows handing work to
// each other — judged against one set of requirements. The same board is the
// Lab's Solution phase (scoped to the app's solution) and the nQube module's
// Solutions view (every solution in the org, including pipeline-only ones).
//
// Verdicts come from the shared checker (data/requirements.js, the generated
// copy of backend/shared/blocks/requirements.js): each pipeline component is
// judged under the org's policy for its env; a solution-level requirement
// aimed at pipeline components is judged on each of those graphs.

const VIA = ['webhook', 'graphql', 'dataset', 'router', 'call', 'manual'];
const newId = (p) => `${p}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

// A starter blueprint: input → validate → match → compute → review → placeholder
// for the system of record. Every block is a cheap deterministic one, so the
// design runs in sandbox the moment it is drawn.
export function blueprintGraph() {
  const b = (id, type, label, config, x, y) => ({ id, type, label, position: { x, y }, config });
  const e = (from, to, port) => ({ id: `${from}-${to}${port ? `-${port}` : ''}`, from, to, ...(port ? { fromPort: port } : {}) });
  return {
    blocks: [
      b('req', 'input.text', 'Request', { text: '{"customer":"Acme","amount":1200,"region":"EU","email":"ops@acme.example"}' }, 0, 120),
      b('check', 'logic.validate', 'Validate request', { rules: 'customer: required\namount: required, number, 1..\nemail: required, email\nregion: required, enum EU|US|APAC' }, 260, 120),
      b('route', 'logic.match', 'Route by region', { field: 'region', table: 'EU: eu-desk\nUS: us-desk\nAPAC: apac-desk', default: 'triage', into: 'desk' }, 520, 40),
      b('fix', 'control.review', 'Fix the request', { instructions: 'The request failed validation (see errors). Correct the fields and approve to continue.', maxWaitMinutes: '480' }, 520, 220),
      b('price', 'logic.compute', 'Price and tier', { assignments: 'priority = amount >= 1000\ntotal = round(amount * 1.2, 2)\ncontact = mask(email)', keep: 'true' }, 780, 120),
      b('approve', 'control.review', 'Desk lead approves', { instructions: 'Approve to record this request in the system of record.', maxWaitMinutes: '480' }, 1040, 120),
      b('record', 'design.placeholder', 'System of record (TBD)', { intent: 'Write the priced request to the ERP. Owner picks the integration.', owner: '', sample: '{"recorded":true}' }, 1300, 120),
    ],
    connections: [
      e('req', 'check'), e('check', 'route', 'valid'), e('check', 'fix', 'invalid'), e('fix', 'route', 'approve'),
      e('route', 'price'), e('price', 'approve'), e('approve', 'record', 'approve'),
    ],
  };
}

// Pipelines are workspace children; the only way to see the org's workflows
// is one query per workspace. A renderless feed per workspace reports up.
function PipelineFeed({ orgId, ws, onDocs }) {
  const { docs } = useEntityList('pipeline', { org: orgId, workspace: ws.id });
  useEffect(() => { if (docs) onDocs(ws.id, docs.map((p) => ({ ...p, workspaceId: ws.id }))); }, [docs, ws.id, onDocs]);
  return null;
}

export function useOrgPipelines(orgId, workspaces) {
  const [byWs, setByWs] = useState({});
  const onDocs = React.useCallback((wsId, docs) => setByWs((cur) => ({ ...cur, [wsId]: docs })), []);
  const pipelines = useMemo(() => Object.values(byWs).flat(), [byWs]);
  const feeds = (workspaces || []).map((ws) => <PipelineFeed key={ws.id} orgId={orgId} ws={ws} onDocs={onDocs} />);
  return { pipelines, feeds };
}

const policyFor = (org, pl) => ({ env: pl?.env || 'sandbox', integrations: [], requirements: org?.pipelinePolicy?.[pl?.env || 'sandbox']?.requirements || [] });

export function pipelineVerdict(org, pl) {
  if (!pl) return null;
  const results = requirementProblems(pl.graph, pl.requirements, policyFor(org, pl), { lastRunMs: pl.lastRun?.ms || null });
  return { results, summary: requirementSummary(results) };
}

const pipelineTo = (orgId, pl) => `/orgs/${orgId}/ws/${pl.workspaceId}/pipelines/${pl.id}`;
const appTo = (orgId, app) => `/orgs/${orgId}/app-builder?app=${app.id}`;

// ---- the board ---------------------------------------------------------------------
// `app` scopes the board to that app's solution (Lab); without it, every
// solution in the org is listed (nQube module). `artifacts` (Lab stories)
// let app components show their acceptance evidence.
export default function SolutionBoard({ org, app = null, artifacts = [], canEdit = false, onPhase = null }) {
  const { user } = useAuth();
  const { docs: solutions } = useEntityList('solution', { org: org.id });
  const { docs: apps } = useEntityList('app', { org: org.id });
  const { docs: workspaces } = useEntityList('workspace', { org: org.id });
  const { pipelines, feeds } = useOrgPipelines(org.id, workspaces);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const mine = useMemo(() => {
    const all = (solutions || []).slice().sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    if (!app) return all;
    return all.filter((s) => s.id === app.links?.solutionId || (s.components || []).some((c) => c.kind === 'app' && c.refId === app.id));
  }, [solutions, app]);
  const solution = mine.find((s) => s.id === picked) || mine[0] || null;

  const run = async (label, fn) => {
    setBusy(label); setErr('');
    try { await fn(); } catch (e) { setErr(friendlyError(e)); } finally { setBusy(''); }
  };
  const patch = (fields) => run('save', () => updateEntity('solution', { org: org.id }, solution.id, fields));

  const createSolution = () => run('create', async () => {
    const name = app ? `${app.name} solution` : 'New solution';
    const components = app ? [{ id: newId('c'), kind: 'app', refId: app.id, workspaceId: app.workspaceId || null, role: 'front door' }] : [];
    if (app?.links?.pipelineId) components.push({ id: newId('c'), kind: 'pipeline', refId: app.links.pipelineId, workspaceId: app.links.workspaceId || null, role: 'workflow' });
    const id = await createEntity('solution', { org: org.id }, {
      name, goal: '', workspaceId: app?.workspaceId || null, components,
      handoffs: components.length === 2 ? [{ id: newId('h'), from: components[0].id, to: components[1].id, via: 'call', note: 'app hands the case to the workflow' }] : [],
      requirements: [], status: 'draft', createdBy: user.uid,
    });
    if (app) await updateEntity('app', { org: org.id }, app.id, { links: { ...(app.links || {}), solutionId: id } });
    setPicked(id);
  });

  if (!solutions || !apps || !workspaces) return <>{feeds}<p className="cs-help">Loading…</p></>;

  return (
    <div>
      {feeds}
      {err && <p className="cs-form-error">{err}</p>}
      {!solution ? (
        <div className="cs-card" style={{ padding: 14 }} data-nova-note={`Solution board, no solution yet${app ? ` for app "${app.name}"` : ''}. NoVA starts one with engineer_solution (components = apps + nQube workflows, handoffs between them, service-level requirements).`}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Engineer the whole service</h3>
          <p className="cs-help" style={{ marginTop: 6, fontSize: 13 }}>
            A solution is the business service end to end: the apps people use and the nQube workflows that
            carry each step, handing work to each other. Name the components, draw the handoffs, then state
            what the service must guarantee — approvals, data integrity, performance — and every workflow is
            checked against it as it is drawn.
          </p>
          {canEdit && <button type="button" className="cs-btn cs-btn-primary" style={{ marginTop: 10 }} disabled={!!busy} onClick={createSolution}>{app ? `Start the solution for ${app.name}` : 'New solution'}</button>}
        </div>
      ) : (
        <Solution
          org={org} app={app} apps={apps} pipelines={pipelines} workspaces={workspaces} artifacts={artifacts}
          solution={solution} siblings={mine} onPick={setPicked} canEdit={canEdit} busy={busy} run={run} patch={patch}
          onNew={createSolution} user={user} onPhase={onPhase}
        />
      )}
    </div>
  );
}

function Solution({ org, app, apps, pipelines, workspaces, artifacts, solution, siblings, onPick, canEdit, busy, run, patch, onNew, user, onPhase }) {
  const components = useMemo(() => solution.components || [], [solution.components]);
  const handoffs = solution.handoffs || [];
  const reqs = solution.requirements || [];
  const [addKind, setAddKind] = useState('');
  const [addRef, setAddRef] = useState('');
  const [hand, setHand] = useState({ from: '', to: '', via: 'call', note: '' });
  const [draft, setDraft] = useState({ name: solution.name, goal: solution.goal || '' });
  useEffect(() => { setDraft({ name: solution.name, goal: solution.goal || '' }); }, [solution.id, solution.name, solution.goal]);

  const appById = Object.fromEntries((apps || []).map((a) => [a.id, a]));
  const plById = Object.fromEntries((pipelines || []).map((p) => [p.id, p]));
  const resolve = (c) => (c.kind === 'app' ? appById[c.refId] : plById[c.refId]) || null;
  const nameOf = (c) => resolve(c)?.name || `${c.kind} ${c.refId.slice(0, 6)}…`;

  // Verdicts per pipeline component, and per solution requirement.
  const verdicts = useMemo(() => Object.fromEntries(components.filter((c) => c.kind === 'pipeline').map((c) => [c.id, pipelineVerdict(org, plById[c.refId])])), [components, plById, org]);
  const stories = (artifacts || []).filter((a) => a.kind === 'story');
  const reqResults = useMemo(() => reqs.map((r) => {
    const targets = (r.componentIds?.length ? components.filter((c) => r.componentIds.includes(c.id)) : components).filter((c) => c.kind === 'pipeline');
    const judged = targets.map((c) => plById[c.refId]).filter(Boolean).map((pl) => requirementProblems(pl.graph, [{ ...r, source: 'pipeline' }], policyFor(org, pl), { lastRunMs: pl.lastRun?.ms || null })[0]).filter(Boolean);
    if (!judged.length) return { id: r.id, status: 'needs-run', detail: targets.length ? 'the workflow could not be loaded' : 'carried by apps — proven by their Lab tests, not by a graph check', blockIds: [] };
    const status = judged.some((j) => j.status === 'unmet') ? 'unmet' : judged.some((j) => j.status === 'needs-run') ? 'needs-run' : 'met';
    return { id: r.id, status, detail: judged.map((j, i) => `${nameOf(targets[i])}: ${j.detail}`).join(' · '), blockIds: [] };
  }), [reqs, components, plById, org]); // eslint-disable-line react-hooks/exhaustive-deps
  const summary = requirementSummary(reqResults);
  const pipelineUnmet = Object.values(verdicts).reduce((n, v) => n + (v?.summary?.unmet || 0), 0);
  // The board describes itself to NoVA (data/describe.js — same text as the
  // solution_status composer): the service map is a React Flow canvas the
  // screen outline cannot walk.
  const novaDescribe = useMemo(
    () => describeSolution(solution, { apps: apps || [], pipelines: pipelines || [], verdicts, rowResults: reqResults, maxChars: 950 }),
    [solution, apps, pipelines, verdicts, reqResults]
  );

  const addComponent = () => {
    if (!addKind || !addRef) return;
    const ref = addKind === 'app' ? appById[addRef] : plById[addRef];
    const c = { id: newId('c'), kind: addKind, refId: addRef, workspaceId: (addKind === 'app' ? ref?.workspaceId : ref?.workspaceId) || null, role: '' };
    patch({ components: [...components, c] }).then(() => { setAddKind(''); setAddRef(''); });
  };
  const newBlueprint = () => run('blueprint', async () => {
    const ws = workspaces.find((w) => w.id === (app?.workspaceId || solution.workspaceId)) || workspaces[0];
    if (!ws) throw new Error('Create a workspace first — workflows live in one.');
    const id = await createEntity('pipeline', { org: org.id, workspace: ws.id }, {
      name: `${solution.name} — service blueprint`, description: 'Service logic drawn as a blueprint: validate, route, compute, approve, record.',
      graph: blueprintGraph(), capabilities: [], mode: 'batch', execution: {}, env: 'sandbox', status: 'draft', createdBy: user.uid,
      requirements: [
        { id: newId('r'), kind: 'integrity.validate-before-write', text: 'Every record is validated before it is written', params: {}, source: 'pipeline' },
        { id: newId('r'), kind: 'approval', text: 'A desk lead approves before the system of record is touched', params: {}, source: 'pipeline' },
      ],
    });
    await updateEntity('solution', { org: org.id }, solution.id, { components: [...components, { id: newId('c'), kind: 'pipeline', refId: id, workspaceId: ws.id, role: 'service logic' }] });
  });
  const removeComponent = (cid) => patch({
    components: components.filter((c) => c.id !== cid),
    handoffs: handoffs.filter((h) => h.from !== cid && h.to !== cid),
    requirements: reqs.map((r) => ({ ...r, componentIds: (r.componentIds || []).filter((x) => x !== cid) })),
  });
  const addHandoff = () => {
    if (!hand.from || !hand.to || hand.from === hand.to) return;
    patch({ handoffs: [...handoffs, { id: newId('h'), ...hand, note: hand.note.trim() }] }).then(() => setHand({ from: '', to: '', via: 'call', note: '' }));
  };

  // NoVA's finger on the board (focus_canvas → `nova:focus`): component ids
  // or a requirement id (its target components) ring on the map; "fit"
  // frames it.
  const [flagged, setFlagged] = useState([]);
  const flowRef = useRef(null);
  useEffect(() => {
    const onFocus = (e) => {
      const d = e.detail || {};
      const known = new Set(components.map((c) => c.id));
      let ids = [];
      if (d.ids?.[0] === 'all') ids = [...known];
      else {
        for (const id of d.ids || []) {
          if (known.has(id)) ids.push(id);
          else {
            const r = reqs.find((x) => x.id === id);
            if (r) ids.push(...(r.componentIds?.length ? r.componentIds : components.filter((c) => c.kind === 'pipeline').map((c) => c.id)));
            else {
              const byName = components.find((c) => nameOf(c).toLowerCase() === id.toLowerCase());
              if (byName) ids.push(byName.id);
            }
          }
        }
      }
      ids = [...new Set(ids)].filter((id) => known.has(id));
      if (d.action === 'fit') {
        flowRef.current?.fitView?.({ padding: 0.3, duration: 400, ...(ids.length && d.ids?.[0] !== 'all' ? { nodes: ids.map((id) => ({ id })) } : {}) });
        d.reply = { ok: true, message: 'Framed the service map.' };
        return;
      }
      if (!ids.length) { d.reply = { ok: false, error: `Nothing on this board matches "${(d.ids || []).join(', ')}" — use a component id or requirement id from the Describe line.` }; return; }
      setFlagged(ids);
      flowRef.current?.fitView?.({ padding: 0.4, duration: 400, nodes: ids.map((id) => ({ id })) });
      window.setTimeout(() => setFlagged((cur) => (cur === ids ? [] : cur)), 3200);
      d.reply = { ok: true, message: `Highlighted ${ids.map((id) => nameOf(components.find((c) => c.id === id))).join(', ')}.`, componentIds: ids };
    };
    window.addEventListener(FOCUS_EVENT, onFocus);
    return () => window.removeEventListener(FOCUS_EVENT, onFocus);
  }, [components, reqs]); // eslint-disable-line react-hooks/exhaustive-deps

  // React Flow map: components as nodes, handoffs as edges, a verdict on each workflow.
  const nodes = components.map((c, i) => {
    const v = verdicts[c.id];
    const tone = c.kind === 'pipeline' ? (!v?.summary?.total ? 'muted' : v.summary.unmet ? 'danger' : v.summary['needs-run'] ? 'warn' : 'ok') : 'info';
    return {
      id: c.id, position: { x: i * 250, y: c.kind === 'app' ? 30 : 150 }, draggable: false, className: flagged.includes(c.id) ? 'nq-flagged' : '',
      data: { label: (
        <div style={{ textAlign: 'left', minWidth: 170 }}>
          <div className="cs-help" style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.04em' }}>{c.kind === 'app' ? 'App' : 'Workflow'}{c.role ? ` · ${c.role}` : ''}</div>
          <strong style={{ fontSize: 12.5 }}>{nameOf(c)}</strong>
          <div style={{ marginTop: 4 }}>
            {c.kind === 'pipeline' ? <span className={`mf-chip is-${tone}`} style={{ fontSize: 10.5 }}>{!v?.summary?.total ? 'no requirements' : `${v.summary.met}/${v.summary.total} met`}</span>
              : <span className="mf-chip is-info" style={{ fontSize: 10.5 }}>{stories.length && app?.id === c.refId ? `${stories.length} stories` : 'app'}</span>}
          </div>
        </div>
      ) },
      style: { borderColor: `var(--${tone === 'ok' ? 'ok' : tone === 'danger' ? 'danger' : tone === 'warn' ? 'warn' : 'border'})`, borderRadius: 10, padding: 8, background: 'var(--surface)' },
    };
  });
  const edges = handoffs.map((h) => ({ id: h.id, source: h.from, target: h.to, label: `${h.via}${h.note ? ` · ${h.note}` : ''}`, markerEnd: { type: MarkerType.ArrowClosed }, style: { stroke: 'var(--text-muted)' }, labelStyle: { fontSize: 10 } }));

  return (
    <div
      style={{ display: 'grid', gap: 14 }}
      data-nova-describe={novaDescribe}
      data-nova-note={`Solution board for "${solution.name}" (id ${solution.id}, status ${solution.status}): ${components.length} component${components.length === 1 ? '' : 's'}, ${handoffs.length} handoff${handoffs.length === 1 ? '' : 's'}, ${summary.total ? `${summary.met} of ${summary.total} requirements met` : 'no requirements yet'}${pipelineUnmet ? `, ${pipelineUnmet} unmet on workflows` : ''}. NoVA edits it with engineer_solution and reads it with solution_status.`}
    >
      <div className="cs-card" style={{ padding: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {siblings.length > 1 && (
          <select className="cs-input" style={{ maxWidth: 240 }} value={solution.id} onChange={(e) => onPick(e.target.value)} aria-label="Solution">
            {siblings.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <input className="cs-input" style={{ flex: 1, minWidth: 180, fontWeight: 600 }} value={draft.name} disabled={!canEdit} onChange={(e) => setDraft({ ...draft, name: e.target.value })} onBlur={() => draft.name.trim() && draft.name !== solution.name && patch({ name: draft.name.trim() })} aria-label="Solution name" />
        <span className={`mf-chip is-${solution.status === 'shipped' ? 'ok' : solution.status === 'engineered' ? 'info' : 'muted'}`}>{solution.status}</span>
        {canEdit && solution.status === 'draft' && summary.total > 0 && summary.allMet && pipelineUnmet === 0 && (
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={!!busy} data-agent-guard="true" title="Declares the design engineered — a person decides this" onClick={() => patch({ status: 'engineered' })}>Mark engineered</button>
        )}
        {canEdit && !app && <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!!busy} onClick={onNew}>+ Solution</button>}
      </div>
      <textarea className="cs-input" rows={2} placeholder="What this service does for whom, in a sentence or two." value={draft.goal} disabled={!canEdit} onChange={(e) => setDraft({ ...draft, goal: e.target.value })} onBlur={() => draft.goal !== (solution.goal || '') && patch({ goal: draft.goal })} aria-label="Solution goal" />

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 340px) minmax(0, 1fr)', gap: 14, alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 14 }}>
          <section className="cs-card" style={{ padding: 12 }}>
            <h3 style={{ margin: 0, fontSize: 14 }}>Components</h3>
            <p className="cs-help" style={{ fontSize: 12, marginTop: 4 }}>The apps and workflows that make up the service.</p>
            {!components.length && <p className="cs-help" style={{ fontSize: 12 }}>None yet.</p>}
            {components.map((c) => {
              const ref = resolve(c);
              const v = verdicts[c.id];
              return (
                <div key={c.id} className="nq-req" style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span className="mf-chip is-muted" style={{ fontSize: 10.5 }}>{c.kind === 'app' ? 'app' : 'workflow'}</span>
                    <strong style={{ fontSize: 12.5, flex: 1 }}>
                      {ref ? <Link to={c.kind === 'app' ? appTo(org.id, ref) : pipelineTo(org.id, ref)}>{ref.name}</Link> : nameOf(c)}
                    </strong>
                    {c.kind === 'pipeline' && v?.summary?.total > 0 && <StatusChip status={v.summary.unmet ? 'unmet' : v.summary['needs-run'] ? 'needs-run' : 'met'} />}
                    {canEdit && <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" title="Remove from the solution" data-agent-guard="true" onClick={() => removeComponent(c.id)}>✕</button>}
                  </div>
                  <input className="cs-input" style={{ marginTop: 4, fontSize: 12 }} placeholder="Role in the service (e.g. intake, adjudication)" defaultValue={c.role || ''} disabled={!canEdit}
                    onBlur={(e) => e.target.value !== (c.role || '') && patch({ components: components.map((x) => (x.id === c.id ? { ...x, role: e.target.value.trim() } : x)) })} />
                  {c.kind === 'pipeline' && ref && (
                    <div className="cs-help" style={{ fontSize: 11.5, marginTop: 2 }}>
                      {(ref.graph?.blocks || []).length} blocks · {ref.env || 'sandbox'} · {v?.summary?.total ? `${v.summary.met} of ${v.summary.total} requirements met` : 'no requirements declared'}
                      {(ref.graph?.blocks || []).some((b) => b.type === 'design.placeholder') ? ' · has placeholders' : ''}
                    </div>
                  )}
                </div>
              );
            })}
            {canEdit && (
              <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <select className="cs-input" value={addKind} onChange={(e) => { setAddKind(e.target.value); setAddRef(''); }} aria-label="Component kind">
                    <option value="">Add…</option>
                    <option value="app">An app</option>
                    <option value="pipeline">A workflow</option>
                  </select>
                  {addKind && (
                    <select className="cs-input" value={addRef} onChange={(e) => setAddRef(e.target.value)} aria-label="Component">
                      <option value="">Choose</option>
                      {(addKind === 'app' ? apps : pipelines).filter((x) => !components.some((c) => c.kind === addKind && c.refId === x.id)).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                    </select>
                  )}
                  {addKind && addRef && <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={!!busy} onClick={addComponent}>Add</button>}
                </div>
                <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={!!busy} onClick={newBlueprint} title="A new workflow drawn from cheap logic blocks — validate, route, compute, approve, record — runnable in sandbox right away">
                  {busy === 'blueprint' ? 'Drawing…' : 'New service blueprint'}
                </button>
              </div>
            )}
          </section>

          <section className="cs-card" style={{ padding: 12 }}>
            <h3 style={{ margin: 0, fontSize: 14 }}>Handoffs</h3>
            <p className="cs-help" style={{ fontSize: 12, marginTop: 4 }}>How work moves between components.</p>
            {handoffs.map((h) => (
              <div key={h.id} style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, fontSize: 12 }}>
                <span style={{ flex: 1 }}>{nameOf(components.find((c) => c.id === h.from) || { kind: '?', refId: h.from })} → {nameOf(components.find((c) => c.id === h.to) || { kind: '?', refId: h.to })} <span className="mf-chip is-muted" style={{ fontSize: 10.5 }}>{h.via}</span>{h.note ? <span className="cs-help"> · {h.note}</span> : null}</span>
                {canEdit && <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" title="Remove this handoff" data-agent-guard="true" onClick={() => patch({ handoffs: handoffs.filter((x) => x.id !== h.id) })}>✕</button>}
              </div>
            ))}
            {canEdit && components.length >= 2 && (
              <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <select className="cs-input" value={hand.from} onChange={(e) => setHand({ ...hand, from: e.target.value })} aria-label="From"><option value="">From</option>{components.map((c) => <option key={c.id} value={c.id}>{nameOf(c)}</option>)}</select>
                  <select className="cs-input" value={hand.to} onChange={(e) => setHand({ ...hand, to: e.target.value })} aria-label="To"><option value="">To</option>{components.map((c) => <option key={c.id} value={c.id}>{nameOf(c)}</option>)}</select>
                  <select className="cs-input" value={hand.via} onChange={(e) => setHand({ ...hand, via: e.target.value })} aria-label="Via">{VIA.map((v) => <option key={v} value={v}>{v}</option>)}</select>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input className="cs-input" placeholder="What is handed over" value={hand.note} onChange={(e) => setHand({ ...hand, note: e.target.value })} />
                  <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={!!busy || !hand.from || !hand.to || hand.from === hand.to} onClick={addHandoff}>Add</button>
                </div>
              </div>
            )}
          </section>
        </div>

        <div style={{ display: 'grid', gap: 14 }}>
          <section className="cs-card" style={{ padding: 0, overflow: 'hidden' }} data-nova-ignore="true" aria-label="Service map">
            <div style={{ height: 300 }}>
              {components.length ? (
                <ReactFlow nodes={nodes} edges={edges} fitView fitViewOptions={{ padding: 0.3 }} nodesConnectable={false} elementsSelectable={false} proOptions={{ hideAttribution: true }} onInit={(inst) => { flowRef.current = inst; }}>
                  <Background gap={16} />
                  <Controls showInteractive={false} />
                </ReactFlow>
              ) : <p className="cs-help" style={{ padding: 14, fontSize: 13 }}>Add components to see the service map.</p>}
            </div>
          </section>

          <section className="cs-card" style={{ padding: 12 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 14, flex: 1 }}>Requirements</h3>
              {summary.total > 0 && <StatusChip status={summary.unmet ? 'unmet' : summary['needs-run'] ? 'needs-run' : 'met'} />}
              <span className="cs-help" style={{ fontSize: 12 }}>{summary.total ? `${summary.met} of ${summary.total} met` : ''}{pipelineUnmet ? ` · ${pipelineUnmet} unmet on workflows` : ''}</span>
            </div>
            <p className="cs-help" style={{ fontSize: 12, marginTop: 4 }}>
              What the service must guarantee. A requirement aimed at workflows is checked on their canvases as they are drawn; one carried by an app is proven by its Lab tests.
              {onPhase && <> Story acceptance lines can become requirements from <button type="button" className="cs-linklike" onClick={() => onPhase('stories')}>Stories</button>.</>}
            </p>
            <RequirementsEditor
              rows={reqs}
              results={reqResults}
              onChange={canEdit ? (rows) => patch({ requirements: rows.map((r) => ({ ...r, componentIds: r.componentIds || [], storyIds: r.storyIds || [] })) }) : () => {}}
              emptyText="No solution requirements yet. Say what the service guarantees — approvals, data integrity, performance — and every workflow component is checked against it."
            />
            {canEdit && reqs.length > 0 && components.length > 0 && (
              <details style={{ marginTop: 10 }}>
                <summary className="cs-help" style={{ fontSize: 12, cursor: 'pointer' }}>Aim requirements at components</summary>
                {reqs.map((r) => (
                  <div key={r.id} style={{ marginTop: 6, fontSize: 12 }}>
                    <strong>{r.text}</strong>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 2 }}>
                      {components.map((c) => (
                        <label key={c.id} className="cs-help" style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>
                          <input type="checkbox" checked={(r.componentIds || []).includes(c.id)} onChange={(e) => patch({ requirements: reqs.map((x) => (x.id === r.id ? { ...x, componentIds: e.target.checked ? [...(x.componentIds || []), c.id] : (x.componentIds || []).filter((y) => y !== c.id) } : x)) })} />
                          {nameOf(c)}
                        </label>
                      ))}
                      <span className="cs-help" style={{ fontSize: 11 }}>{(r.componentIds || []).length ? '' : '(all workflows)'}</span>
                    </div>
                  </div>
                ))}
              </details>
            )}
          </section>

          {components.filter((c) => c.kind === 'pipeline').map((c) => {
            const v = verdicts[c.id]; const ref = plById[c.refId];
            if (!ref || !v?.results?.length) return null;
            return (
              <section key={c.id} className="cs-card" style={{ padding: 12 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <h3 style={{ margin: 0, fontSize: 13.5, flex: 1 }}>{ref.name} <span className="cs-help" style={{ fontWeight: 400 }}>— its own requirements</span></h3>
                  <Link to={pipelineTo(org.id, ref)} className="cs-btn cs-btn-outline cs-btn-sm">Open canvas</Link>
                </div>
                {v.results.map((r) => (
                  <div key={r.id} className={`nq-req is-${r.status}`} style={{ marginTop: 6 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <StatusChip status={r.status} />
                      <strong style={{ fontSize: 12, flex: 1 }}>{r.text}</strong>
                      <span className="mf-chip is-muted" style={{ fontSize: 10.5 }}>{r.source}</span>
                      {r.storyId && <span className="mf-chip is-info" style={{ fontSize: 10.5 }} title={r.storyId}>story</span>}
                    </div>
                    <div className="cs-help" style={{ fontSize: 11.5, marginTop: 2 }}>{r.detail}</div>
                  </div>
                ))}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
